import { useCallback, useEffect, useState } from 'react';
import type { PhieuChiDetailRow, PhieuChiFormInitialData, PurchaseInvoiceSource } from '../types';
import { useAuth } from '@/contexts/auth-context';
import { useSearchParams } from '@/routing';
import { QUERY_KEYS } from '@/constants';
import api from '@/lib/api';

const scalarValue = (value: unknown): string | number | undefined =>
  typeof value === 'string' || typeof value === 'number' ? value : undefined;

interface UseEditPhieuChiWithUpdatedInvoiceReturn {
  shouldEditWithUpdatedData: boolean;
  mergedFormData: PhieuChiFormInitialData | null;
  editPhieuChiUuid: string | null;
  isLoading: boolean;
  error: string | null;
}

/**
 * Hook để xử lý edit phiếu chi với dữ liệu hóa đơn đã được cập nhật
 * Sử dụng khi có query parameters: edit=<phieu_chi_uuid>&hdmdv=<hoa_don_uuid>
 */
export function useEditPhieuChiWithUpdatedInvoice(): UseEditPhieuChiWithUpdatedInvoiceReturn {
  const searchParams = useSearchParams();
  const { entity } = useAuth();

  const editPhieuChiUuid = searchParams.get('edit');
  const hdmdvParam = searchParams.get('hdmdv');
  const hdmhParam = searchParams.get('hdmh');

  const [shouldEditWithUpdatedData, setShouldEditWithUpdatedData] = useState(false);
  const [mergedFormData, setMergedFormData] = useState<PhieuChiFormInitialData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPhieuChiByUuid = useCallback(
    async (uuid: string) => {
      if (!entity?.slug) throw new Error('Entity not found');

      const response = await api.get(`/entities/${entity.slug}/erp/${QUERY_KEYS.PHIEU_CHI}/${uuid}/`);
      return response.data as PhieuChiFormInitialData;
    },
    [entity?.slug]
  );

  const fetchUpdatedInvoiceData = useCallback(
    async (uuid: string, isHoaDonMuaHang: boolean = false) => {
      if (!entity?.slug) throw new Error('Entity not found');

      const cacheKey = `hoa_don_detail_${uuid}`;
      const cachedData = sessionStorage.getItem(cacheKey);
      if (cachedData) {
        sessionStorage.removeItem(cacheKey);
        return JSON.parse(cachedData) as PurchaseInvoiceSource;
      }

      const endpoint = isHoaDonMuaHang ? QUERY_KEYS.HOA_DON_MUA_HANG_TRONG_NUOC : QUERY_KEYS.HOA_DON_MUA_DICH_VU;
      const response = await api.get(`/entities/${entity.slug}/erp/${endpoint}/${uuid}/`);
      return response.data as PurchaseInvoiceSource;
    },
    [entity?.slug]
  );

  const mergePhieuChiWithUpdatedInvoice = useCallback(
    (
      phieuChiData: PhieuChiFormInitialData,
      updatedInvoiceData: PurchaseInvoiceSource
    ): PhieuChiFormInitialData | null => {
      if (!phieuChiData || !updatedInvoiceData) return null;

      const updatedChiTiet =
        phieuChiData.chi_tiet?.map((item: PhieuChiDetailRow, index: number) => {
          const updatedDetail = updatedInvoiceData.chi_tiet_data?.[index] || updatedInvoiceData.chi_tiet?.[index];
          if (updatedDetail) {
            return {
              ...item,
              dien_giai: updatedInvoiceData.dien_giai,

              gia_nt:
                scalarValue(updatedDetail.gia_nt2) ||
                scalarValue(updatedDetail.gia_nt) ||
                scalarValue(updatedDetail.gia) ||
                0,
              gia:
                scalarValue(updatedDetail.gia2) ||
                scalarValue(updatedDetail.gia) ||
                scalarValue(updatedDetail.gia_nt2) ||
                scalarValue(updatedDetail.gia_nt) ||
                0,
              tien_nt:
                scalarValue(updatedDetail.tien_nt2) ||
                scalarValue(updatedDetail.tien_nt) ||
                scalarValue(updatedDetail.tien) ||
                0,
              tien:
                scalarValue(updatedDetail.tien2) ||
                scalarValue(updatedDetail.tien) ||
                scalarValue(updatedDetail.tien_nt2) ||
                scalarValue(updatedDetail.tien_nt) ||
                0,

              so_luong: scalarValue(updatedDetail.so_luong) || scalarValue(item.so_luong) || 0,

              thue_nt: scalarValue(updatedDetail.thue_nt) || scalarValue(updatedDetail.thue) || 0,
              thue: scalarValue(updatedDetail.thue) || scalarValue(updatedDetail.thue_nt) || 0,
              thue_suat: scalarValue(updatedDetail.thue_suat) || item.thue_suat || 0,

              tl_ck: scalarValue(updatedDetail.tl_ck) || scalarValue(item.tl_ck) || 0,
              ck_nt: scalarValue(updatedDetail.ck_nt) || scalarValue(updatedDetail.ck) || 0,
              ck: scalarValue(updatedDetail.ck) || scalarValue(updatedDetail.ck_nt) || 0,

              ty_gia2: String(updatedInvoiceData.ty_gia || item.ty_gia2 || 1),

              id_hd_data: {
                ...item.id_hd_data,
                so_ct: updatedInvoiceData.so_ct,
                ngay_ct: updatedInvoiceData.ngay_ct,
                ty_gia: updatedInvoiceData.ty_gia || 1,
                tien_tren_hd: updatedInvoiceData.t_tt,
                tien_hd_nt: updatedInvoiceData.t_tt_nt || updatedInvoiceData.t_tt,
                tien_con_phai_tt: updatedInvoiceData.t_tt,
                ngoai_te: updatedInvoiceData.ma_nt_data?.ma_nt || updatedInvoiceData.ma_nt
              }
            } as unknown as PhieuChiDetailRow;
          }
          return item;
        }) || [];

      return {
        ...phieuChiData,
        ngay_ct0: updatedInvoiceData.ngay_ct,
        so_ct0: updatedInvoiceData.so_ct,
        dien_giai: updatedInvoiceData.dien_giai,

        ma_kh: updatedInvoiceData.ma_kh_data?.uuid || updatedInvoiceData.ma_kh,
        ong_ba: updatedInvoiceData.ma_kh_data?.contact_person || updatedInvoiceData.ong_ba,
        dia_chi: updatedInvoiceData.ma_kh_data?.address || updatedInvoiceData.dia_chi,

        ma_nt: updatedInvoiceData.ma_nt || 'VND',
        ty_gia: String(updatedInvoiceData.ty_gia || 1),

        chi_tiet: updatedChiTiet,

        t_tien_nt: String(updatedInvoiceData.t_tien_nt || updatedInvoiceData.t_tien || 0),
        t_tien: String(updatedInvoiceData.t_tien || 0),
        t_thue_nt: String(updatedInvoiceData.t_thue_nt || updatedInvoiceData.t_thue || 0),
        t_thue: String(updatedInvoiceData.t_thue || 0),
        t_ck_nt: String(updatedInvoiceData.t_ck_nt || updatedInvoiceData.t_ck || 0),
        t_ck: String(updatedInvoiceData.t_ck || 0),
        t_tt_nt: String(updatedInvoiceData.t_tt_nt || updatedInvoiceData.t_tt || 0),
        t_tt: String(updatedInvoiceData.t_tt || 0)
      };
    },
    []
  );

  useEffect(() => {
    const invoiceUuid = hdmdvParam || hdmhParam;
    const isHoaDonMuaHang = !!hdmhParam;

    if (editPhieuChiUuid && invoiceUuid) {
      setShouldEditWithUpdatedData(true);
      setIsLoading(true);
      setError(null);

      Promise.all([fetchPhieuChiByUuid(editPhieuChiUuid), fetchUpdatedInvoiceData(invoiceUuid, isHoaDonMuaHang)])
        .then(([phieuChiData, updatedInvoiceData]) => {
          const merged = mergePhieuChiWithUpdatedInvoice(phieuChiData, updatedInvoiceData);
          setMergedFormData(merged);
        })
        .catch(() => {
          setError('Không thể tải dữ liệu phiếu chi hoặc hóa đơn');
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setShouldEditWithUpdatedData(false);
      setMergedFormData(null);
      setError(null);
    }
  }, [
    editPhieuChiUuid,
    hdmdvParam,
    hdmhParam,
    fetchPhieuChiByUuid,
    fetchUpdatedInvoiceData,
    mergePhieuChiWithUpdatedInvoice
  ]);

  return {
    shouldEditWithUpdatedData,
    mergedFormData,
    editPhieuChiUuid,
    isLoading,
    error
  };
}
